import { z } from 'zod';

import { PaginationSchema } from '../../pagination.js';

const CentsSchema = z.string().regex(/^\d+$/);
const QueryBoolSchema = z
  .union([z.boolean(), z.enum(['true', 'false'])])
  .optional()
  .transform((value) => (value === undefined ? undefined : value === true || value === 'true'));

export const PriceOverrideTargetKindSchema = z.enum(['SKU', 'BRAND', 'CATEGORY']);
export const PriceOverridePricingKindSchema = z.enum(['MARKUP', 'FIXED']);

export const ListPlacePriceSimulatorQuerySchema = PaginationSchema.extend({
  q: z.string().trim().min(1).max(100).optional(),
  missingOnly: QueryBoolSchema,
  exceptionOnly: QueryBoolSchema,
  promotionOnly: QueryBoolSchema,
});

export const PlacePriceSimulatorExceptionSchema = z.object({
  id: z.uuid(),
  targetKind: PriceOverrideTargetKindSchema,
  targetId: z.uuid(),
  pricingKind: PriceOverridePricingKindSchema,
  markupBp: z.number().int().nullable(),
  sellingUnitCents: CentsSchema.nullable(),
});

export const PlacePriceSimulatorPromotionSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  targetKind: PriceOverrideTargetKindSchema,
  targetId: z.uuid(),
  discountBp: z.number().int().min(0).max(10_000),
  validFrom: z.iso.datetime(),
  validUntil: z.iso.datetime(),
});

export const PlacePriceSimulatorItemSchema = z.object({
  placeProductId: z.uuid(),
  tenantProductId: z.uuid(),
  sku: z.string().nullable(),
  name: z.string(),
  brandId: z.uuid().nullable(),
  brandName: z.string().nullable(),
  categoryId: z.uuid().nullable(),
  categoryName: z.string().nullable(),
  unitCostCents: CentsSchema.nullable(),
  costBasis: z.enum(['WEIGHTED_AVAILABLE', 'LAST_INBOUND', 'LAST_N_INBOUND']).nullable(),
  markupBp: z.number().int().nullable(),
  bandMinCents: CentsSchema.nullable(),
  bandMaxCents: CentsSchema.nullable(),
  liveSellingCents: CentsSchema.nullable(),
  tableSellingCents: CentsSchema.nullable(),
  tableCalculatedAt: z.iso.datetime().nullable(),
  missingPrice: z.boolean(),
  marginBp: z.number().int().nullable(),
  exception: PlacePriceSimulatorExceptionSchema.nullable(),
  promotion: PlacePriceSimulatorPromotionSchema.nullable(),
  effectiveSellingCents: CentsSchema.nullable(),
});

export const ListPlacePriceSimulatorResponseSchema = z.object({
  items: z.array(PlacePriceSimulatorItemSchema),
  pagination: z.object({
    cursor: z.string().nullable(),
    hasMore: z.boolean(),
    limit: z.number().int().positive(),
  }),
});

export const ApplyPlacePriceSimulatorBodySchema = z
  .object({
    q: z.string().trim().min(1).max(100).optional(),
    missingOnly: z.boolean().optional(),
    exceptionOnly: z.boolean().optional(),
    promotionOnly: z.boolean().optional(),
    placeProductIds: z.array(z.uuid()).min(1).max(500).optional(),
    excludePlaceProductIds: z.array(z.uuid()).min(1).max(500).optional(),
  })
  .refine(
    (body) => body.placeProductIds === undefined || body.excludePlaceProductIds === undefined,
    { message: 'placeProductIds and excludePlaceProductIds are mutually exclusive' },
  )
  .default({});

export const ApplyPlacePriceSimulatorResponseSchema = z.object({
  appliedCount: z.number().int().nonnegative(),
});

export const PinPlacePriceExceptionBodySchema = z.discriminatedUnion('pricingKind', [
  z.object({
    targetKind: PriceOverrideTargetKindSchema,
    targetId: z.uuid(),
    pricingKind: z.literal('MARKUP'),
    markupBp: z.number().int().nonnegative(),
  }),
  z.object({
    targetKind: PriceOverrideTargetKindSchema,
    targetId: z.uuid(),
    pricingKind: z.literal('FIXED'),
    sellingUnitCents: CentsSchema,
  }),
]);

export const PlacePriceExceptionResponseSchema = z.object({
  id: z.uuid(),
  targetKind: PriceOverrideTargetKindSchema,
  targetId: z.uuid(),
  pricingKind: PriceOverridePricingKindSchema,
  markupBp: z.number().int().nullable(),
  sellingUnitCents: CentsSchema.nullable(),
});

export type ListPlacePriceSimulatorQuery = z.infer<typeof ListPlacePriceSimulatorQuerySchema>;
export type PlacePriceSimulatorException = z.infer<typeof PlacePriceSimulatorExceptionSchema>;
export type PlacePriceSimulatorPromotion = z.infer<typeof PlacePriceSimulatorPromotionSchema>;
export type PlacePriceSimulatorItem = z.infer<typeof PlacePriceSimulatorItemSchema>;
export type ListPlacePriceSimulatorResponse = z.infer<typeof ListPlacePriceSimulatorResponseSchema>;
export type ApplyPlacePriceSimulatorBody = z.infer<typeof ApplyPlacePriceSimulatorBodySchema>;
export type ApplyPlacePriceSimulatorResponse = z.infer<
  typeof ApplyPlacePriceSimulatorResponseSchema
>;
export type PinPlacePriceExceptionBody = z.infer<typeof PinPlacePriceExceptionBodySchema>;
export type PlacePriceExceptionResponse = z.infer<typeof PlacePriceExceptionResponseSchema>;
